import {Injectable} from '@angular/core';
import {apiEndpoints, CommonConstant, platformIconPath} from '../../app.constants';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs/Observable';
import {CampaignCustomTerm, CampaignPostStatus, DeletePostCampaign, CampaignInfluencerSearch, Campaign} from '../../../models/campaign';
import {KeywordType, Mention} from '../../../models/mention';
import {CampaignSave} from '../../../models/campaign-save';
import {BackendHttpService} from '../../core/backend-http.service';
import {Router} from '@angular/router';
import {ErrorTemplate} from '../../../models/ErrorTemplate';
import {StringUtils} from '../../utils/string.utils';
import {ValidationHandlerService} from '../../core/validation-handler.service';
import {InfluencerCampaign} from '../../../models/influencer-campaign';
import {CommonService} from '../common/common.service';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import { GroupSearch } from '../../../models/group-search';
import { Influencer } from '../../../models/influencer';

@Injectable()
export class CampaignService extends BackendHttpService<any> {

  constructor(
    public router: Router,
    public translate: TranslateService,
    private validationHandler: ValidationHandlerService,
    private commonService: CommonService,
    private http: HttpClient
  ) {
    super(router, translate);
  }

  public getCampaignFullListForCompany(companyId, isCampaignHistory) {
    const apiUrl = apiEndpoints.campaigns.replace(':companyId', companyId);
    const status = isCampaignHistory ? 2 : 1;
    return this.get(apiUrl + `?status=${status}`).map((res) => res ? res.data : null);
  }

  public searchCampaigns(companyId: number, isCampaignHistory: boolean, searchValue?: string): Observable<any> {
    const apiUrl = apiEndpoints.searchCampaigns;
    const params = {
      companyId,
      searchValue,
      campaignStatus: isCampaignHistory ? 'Archived' : 'Active'
    };
    return this.get(apiUrl, params).map((res) => res ? res.data : null);
  }

  public getCampaignShortListForCompany(companyId) {
    const apiUrl = apiEndpoints.getCampaignShortListForCompany;
    const params = {
      companyId: companyId,
      status: 1
    };
    return this.get(apiUrl, params).map((res) => res ? res.data : []);
  }

  public getInfluencerByCampaign(campaignId) {
    return this.get(apiEndpoints.campaignInfluencers.replace(':campaignId', campaignId)).map(res => res.data);
  }

  public deleteCampaign(campaignId) {
    return this.delete(apiEndpoints.deleteCampaign + '/' + campaignId);
  }

  /**
   * this method is used to add an influencer to Campaign
   * @param {AddInfluencersCampaign} addInfluencerToCampaign
   * @returns {Observable<any>}
   */
  public addInfluencerToCampaign(influencerToAdd) {
    return this.post(apiEndpoints.addInfluencerToCampaign, influencerToAdd);
  }

  public getSelectableCampaignTerms(companyid, campaignType) {
    return this.get(apiEndpoints.getSelectableCampaignTerms + '?companyId=' + companyid + '&campaignType=' + campaignType)
      .map(resp => resp.data);
  }

  public addNewCampaign(campaign: CampaignSave) {
    const errors = this.validateCampaign(campaign);
    if (errors.length > 0) {
      this.validationHandler.shouldDisplayErrors(true);
      this.validationHandler.updateErrors(errors);
      throw new Error('add_new_campaign_error');
    } else {
      return this.post(apiEndpoints.addNewCampaign, campaign)
        .map(resp => resp.data);
    }
  }

  public updateCampaign(campaign: any) {
    const errors = this.validateCampaign(campaign);
    if (errors.length > 0) {
      this.validationHandler.shouldDisplayErrors(true);
      this.validationHandler.updateErrors(errors);
      return Observable.throw(new Error('update_campaign_error'));
    } else {
      return this.post(apiEndpoints.updateCampaign, campaign)
        .map(resp => resp.data);
    }
  }

  private validateCampaign(campaign): ErrorTemplate[] {
    const errors: ErrorTemplate[] = [];
    if (!campaign.brief || StringUtils.trim(campaign.brief) === '') {
      errors.push({name: 'campaign_brief',
        message: this.translate.instant('campaigns.validation.brief_madatory')});
    } else if (campaign.brief.length > 21844) {
      errors.push({name: 'campaign_brief',
        message: this.translate.instant('campaigns.validation.brief_too_long')});
    }

    if (campaign.keywordIds.length === 0) {
      errors.push({name: 'campaign_keywords',
        message: this.translate.instant('campaigns.validation.mention_madatory')});
    }

    if (campaign.additionalInfo && campaign.additionalInfo.length > 21844) {
      errors.push({name: 'campaign_additional_info',
        message: this.translate.instant('campaigns.validation.brief_too_long')});
    }
    return errors;
  }

  public uploadFile(formData: FormData) {
    return this.postRaw(apiEndpoints.uploadFile, formData, { headers: null })
      .map(resp => resp.data);
  }

  public getCampaignFiles(campaignId) {
    return this.get(apiEndpoints.getCampaignFiles.replace(':CAMPAIGN_ID', campaignId))
      .map(resp => resp.data);
  }

  public getCampaignTerms(campaignId) {
    return this.get(apiEndpoints.getCampaignTerms.replace(':CAMPAIGN_ID', campaignId))
      .map(resp => resp.data);
  }

  public getCampaign(campaignId, companyId) {
    return this.get(apiEndpoints.getCampaign + '/' + campaignId, {companyId}).map(res => res.data);
  }
  public getCampaignOfInfluencer(influencerId, campaignId) {
    return this.get(apiEndpoints.getAllCampaignsForInfluencer, {
      influencer_id: influencerId
    }).map((res: any) => Array.isArray(res.data) ? res.data.filter(cp => cp.campaign_id === campaignId) : []);
  }
  public parseMentions(keyword: string): Mention {
    // regression use to detect a keywork is matching with url
    const UrlPattern = new RegExp(/^(?!:\/\/)([a-zA-Z0-9]+\.)?[a-zA-Z0-9-]+\.([a-zA-Z]{2,6}|[a-zA-Z]{2,6}.[a-zA-Z]{2,6})?$/i);
    let type;

    if (!UrlPattern.test(keyword) && keyword.indexOf('#') !== 0) {
      type = KeywordType.Keyword;
    } else if (UrlPattern.test(keyword) && keyword.indexOf('#') !== 0) {
      type = KeywordType.URL;
    } else if (!UrlPattern.test(keyword) && keyword.indexOf('#') === 0) {
      type = KeywordType.Hashtag;
    }

    return {
      keyword: StringUtils.trim(keyword),
      keywordType: type,
      language: []
    };
  }

  public getAvailableCampaignsToAddPost(companyId) {
    return this.get(apiEndpoints.getAvailableCampaignsToAddPost.replace(':companyId', companyId)).map(res =>
      res && res.data ? res.data.map((item) => Object.assign({}, item, {
        campaign_id: item.id,
        campaign_name: item.name
      })) : []
    );
  }

  /**
   * this method is used to add posts to Campaign
   * @param {posts: [{postUrlHash: <url hash of post>},…]} postParams
   * @returns {Observable<any>}
   */
  public addPostsToCampaign(campaignId, postParams): Observable<any> {
    return this.post(apiEndpoints.addPostsToCampaign.replace(':campaignId', campaignId), {posts: postParams});
  }

  /**
   * get campaign post status
   * @param campaignId
   * @param postUrlHashs
   * @returns {Observable<any>}
   */
  public checkPostStatus(campaignId, postUrlHashs): Observable<any> {
    return this.post(apiEndpoints.getPostStatus.replace(':campaignId', campaignId), postUrlHashs)
      .map(res => res && res.data ? res.data : null);
  }

  /**
   * this method is used to delete an Influencer from Campaign
   * @param {campaignInfluencerId}
   * @returns {Observable<any>}
   */
  public deleteInfluencerFromCampaign(campaignInfluencerId): Observable<any> {
    return this.delete(apiEndpoints.deleteInfluencerFromCampaign + campaignInfluencerId);
  }

  /**
   * validate a file before uploading
   * @param file
   */
  public validateMaterialUploadFile(file) {
    const fileSize = file.size / 1024 / 1024;
    if (CommonConstant.MAX_UPLOADED_FILE_SIZE < fileSize) {
      throw new Error('common.file_size_too_large');
    }
  }

  public downloadCampaignMaterials(fileData): Observable<Blob> {
    let _headers = new HttpHeaders();
    _headers = _headers.set('Content-Type', `${fileData.contentType}`);
    return this.http.get(fileData.content, {responseType: 'blob', headers: _headers, params: fileData.name});
  }

  /**
   * this method is used to delete custom term
   * @param {termId}
   * @returns {Observable<any>}
   */
  public deleteTerm(id) {
    return this.delete(apiEndpoints.deleteCampaignTerm + id)
      .map(resp => resp.data);
  }

  /**
   * this method is used to add custom term
   * @param {data}
   * @returns {Observable<any>}
   */
  public addCustomTerm(data) {
    return this.post(apiEndpoints.addCustomTerm, data)
      .map(resp => resp.data);
  }

  /**
   * this method is used to edit custom term
   * @param {data}
   * @returns {Observable<any>}
   */
  public editCustomTerm(data, termId) {
    return this.put(apiEndpoints.updateCampaignTerm + termId, data)
      .map(resp => resp.data);
  }

  public validateCustomTerm(term: CampaignCustomTerm): ErrorTemplate[] {
    const errors: ErrorTemplate[] = [];
    if (!term.summary || StringUtils.trim(term.summary) === '') {
      errors.push({name: 'headline',
        message: this.translate.instant('campaigns.term.headline_madatory')});
    }
    if (!term.body || StringUtils.trim(term.body) === '') {
      errors.push({name: 'body_text',
        message: this.translate.instant('campaigns.term.body_text_madatory')});
    }
    return errors;
  }

  public downloadInvoiceDocument(fileId: number, fileName: string) {
    return this.getImage(apiEndpoints.getUploadFile + '/' + fileId, {fileName: fileName});
  }

  /**
   * get status of post list based on urlHash
   * @param campaignId
   * @param postUrlHashs
   * @returns {Observable<any>}
   */
  public getPostStatus(campaignId: number, postUrlHashs: string[]) {
    return this.post(apiEndpoints.getPostStatus.replace(':campaignId', campaignId.toString()), {
      posts: postUrlHashs.map(h => ({
        urlHash: h
      }))
    }).map(res => res && res.data ? res.data : null);
  }

  /**
   * Delete a list of post based on {urlHash, channelId}
   * @param {number} campaignId
   * @param {DeletePostCampaign[]} posts
   * @returns {Observable<any>}
   */
  public deletePostFromCampaign(campaignId: number, posts: DeletePostCampaign[]) {
    return this.post(apiEndpoints.deletePost.replace(':campaignId', campaignId.toString()), {
      posts
    }).map(res => res && res.data ? res.data : null);
  }

  /**
   * mapping ES data from WS to Campaign Influencer model
   * @param influencer
   * @returns {InfluencerCampaign}
   */
  public mapInfluencerCampaign(influencer: any): InfluencerCampaign {
    const influencerCamp: InfluencerCampaign = new InfluencerCampaign();
    influencerCamp.campaign_influencer_id = influencer.campaignInfluencerId;
    influencerCamp.country = influencer.country;
    influencerCamp.influencer_id = influencer.influencerId;
    influencerCamp.influencer_name = influencer.influencerName;
    influencerCamp.campaign_influencer_status = influencer.status;
    influencerCamp.influencer_profile_picture = influencer.profileImage;
    influencerCamp.country_code = influencer.country.code;
    influencerCamp.campaign_influencer_engagement = {
      value: influencer.engagement,
      percent: null
    };
    influencerCamp.campaign_influencer_impact = {
      value: influencer.impact,
      percent: null
    };
    influencerCamp.campaign_influencer_reach = {
      value: influencer.reach,
      percent: null
    };
    influencerCamp.campaign_influencer_number_of_post = influencer.totalPost;
    influencerCamp.campaign_influencer_platformBlogs = this.hasPlatform(platformIconPath.blogs.id, influencer);
    influencerCamp.campaign_influencer_platformFacebook = this.hasPlatform(platformIconPath.facebook.id, influencer);
    influencerCamp.campaign_influencer_platformInstagram = this.hasPlatform(platformIconPath.instagram.id, influencer);
    influencerCamp.campaign_influencer_platformPinterest = this.hasPlatform(platformIconPath.pinterest.id, influencer);
    influencerCamp.campaign_influencer_platformSnapchat = this.hasPlatform(platformIconPath.snapchat.id, influencer);
    influencerCamp.campaign_influencer_platformTwitter = this.hasPlatform(platformIconPath.twitter.id, influencer);
    influencerCamp.campaign_influencer_platformYoutube = this.hasPlatform(platformIconPath.youtube.id, influencer);
    influencerCamp.campaign_influencer_platformTikTok = this.hasPlatform(platformIconPath.tiktok.id, influencer);
    return influencerCamp;
  }

  public updateCampaignDetailsData(camp, influencerList) {
    if (!camp) {
      return null;
    }
    camp.isFetchingKpi = false;
    camp.influencers = influencerList.map(infl => this.mapInfluencerCampaign(infl));
    // reduce to calculate campaign global KPI
    const campaignKpis = camp.influencers.reduce(
      // Reduce function
      (accumulator, infl_kpi) => ({
        impression: accumulator.impression,
        engagement: accumulator.engagement + infl_kpi.campaign_influencer_engagement.value,
        reach:      accumulator.reach + infl_kpi.campaign_influencer_reach.value,
        totalPost:  accumulator.totalPost + infl_kpi.campaign_influencer_number_of_post
      }),
      // Initial state of accumulator
      {
        impression: 0,
        engagement: 0,
        reach: 0,
        totalPost: 0,
      }
    );
    if (campaignKpis) {
      camp.engagement = campaignKpis.engagement;
      camp.impression = campaignKpis.impression;
      camp.reach = campaignKpis.reach;
      camp.impact = (campaignKpis.engagement / campaignKpis.reach).toFixed(2);
    }
    camp.numberOfInfluencers = camp.influencers.length;
    camp.numberOfPosts = campaignKpis.totalPost;
  }

  public updateCampaignDetailsDataByPostList(camp) {
    if (!camp) {
      return null;
    }
    camp.isFetchingKpi = false;
    // reduce to calculate campaign global KPI
    const campaignPostKpi = camp.posts.reduce(
      // Reduce function
      (accumulator, postkpi) => ({
        impression: accumulator.impression + postkpi.impression,
        engagement: accumulator.engagement + postkpi.engagement,
        reach:      accumulator.reach + postkpi.reach
      }),
      // Initial state of accumulator
      {
        impression: 0,
        engagement: 0,
        reach: 0,
      }
    );
    if (campaignPostKpi) {
      camp.engagement = campaignPostKpi.engagement;
      camp.impression = campaignPostKpi.impression;
      camp.reach = campaignPostKpi.reach;
      camp.impact = (campaignPostKpi.engagement / campaignPostKpi.reach).toFixed(2);
    }
    camp.numberOfPosts = camp.posts.length;
  }

  private hasPlatform(platformId, influencer) {
    if (!Array.isArray(influencer.platformIds) || influencer.platformIds.length === 0) {
      return false;
    }
    return influencer.platformIds.indexOf(platformId) >= 0;
  }

  public getCampaignPostDailyKpis(campaignInfluencerId: number, fromDate: string, toDate: string) {
    const params = {
      fromDate,
      toDate
    };
    return this.get(apiEndpoints.campaignPostDailyKpis.replace(':campaignInfluencerId', campaignInfluencerId.toString()), params).map(res => {
      return this.commonService.normalizeDataForActivityLineChart(res.data.data);
    });
  }
  public archiveCampaign(campaignId: number) {
    return this.post(
      apiEndpoints.archiveCampaign,
      {
        id: campaignId
      }).map(res => console.log(res.data));
  }

  public unarchiveCampaign(campaignId: number) {
    return this.post(
      apiEndpoints.unarchiveCampaign,
      {
        id: campaignId
      }).map(res => res.data);
  }

  public searchCampaignInfluencers(searchFilter, campaignId): Observable<any> {
    const searchFilterOrderBy = searchFilter.orderBy ? searchFilter.orderBy.toLowerCase() : CommonConstant.SORT_BY_REACH;
    searchFilter.isAscendingOrder = searchFilter.orderBy && searchFilter.isAscendingOrder ? searchFilter.isAscendingOrder : CommonConstant.DEFAULT_ASCENDING_ORDER_SORT_BY;
    searchFilter.limit = searchFilter.limit ? searchFilter.limit : 0;
    const mappedSearchFilter = this.mappingCampaignInfluencerSearch(searchFilter);
    const result = this.post(apiEndpoints.searchCampaignInfluencer.replace(':campaignId', campaignId)
                    + `?orderBy=${searchFilterOrderBy}&isAscendingOrder=${searchFilter.isAscendingOrder}&limit=${searchFilter.limit}`,
                    JSON.stringify(mappedSearchFilter));
    return result.map(res => res && res.data ? res.data : []);
  }

  private mappingCampaignInfluencerSearch(searchFilter) {
    const searchParam: CampaignInfluencerSearch = new CampaignInfluencerSearch();
    searchParam.countries = searchFilter.selectedCountry ? [searchFilter.selectedCountry] : [];
    searchParam.genders = searchFilter.selectedGender ? [searchFilter.selectedGender] : searchFilter.selectedGender === 0 ? [0] : [];
    searchParam.platforms = searchFilter.selectedPlatform ? [searchFilter.selectedPlatform] : [];
    searchParam.searchString = searchFilter.searchString ? searchFilter.searchString : null;
    searchParam.reachFrom = searchFilter.reachFrom ? searchFilter.reachFrom : null;
    searchParam.reachTo = searchFilter.reachTo ? searchFilter.reachTo : null;
    searchParam.engagementFrom = searchFilter.engagementFrom ? searchFilter.engagementFrom : null;
    searchParam.engagementTo = searchFilter.engagementTo ? searchFilter.engagementTo : null;
    searchParam.impactFrom = searchFilter.impactFrom ? searchFilter.impactFrom : null;
    searchParam.impactTo = searchFilter.impactTo ? searchFilter.impactTo : null;
    return searchParam;
  }

  public getCampaignInfluencerPostCount(campaignId, periodRange) {
    return this.get(apiEndpoints.getCampaignInfluencerPostCount.replace(':campaignId', campaignId) + `?period=${periodRange}`).map(res => res.data);
  }

  public mapNewAddedInfluencer(newInfluencerData, filteredNewAcceptInfluencer?) {
    if (!newInfluencerData) {
      return;
    }
    return newInfluencerData.map(itemData => {
      const newAddedInfluencer: Influencer = new Influencer();
      newAddedInfluencer.country = itemData.country ? itemData.country : CommonConstant.INTERNATIONAL_COUNTRY_DATA;
      newAddedInfluencer.influencer_id = itemData.influencer_id;
      newAddedInfluencer.influencer_name = itemData.influencer_name;
      newAddedInfluencer.profilePicture = itemData.influencer_profile_picture;
      newAddedInfluencer.influencer_image = itemData.influencer_profile_picture;
      newAddedInfluencer.country_code = itemData.country && itemData.country.code ? itemData.country.code : CommonConstant.INTERNATION_COUNTRY_CODE.toUpperCase();
      newAddedInfluencer.engagement = itemData.engagement;
      newAddedInfluencer.impact = filteredNewAcceptInfluencer && filteredNewAcceptInfluencer.length === 0 ? 0 : itemData.impact && isNaN(itemData.impact) === false ? itemData.impact * 100 : 0;
      newAddedInfluencer.reach = filteredNewAcceptInfluencer && filteredNewAcceptInfluencer.length === 0 ? 0 : itemData.reach;
      newAddedInfluencer.numberOfPosts = filteredNewAcceptInfluencer && filteredNewAcceptInfluencer.length === 0 ? 0 : itemData.numberOfPosts;
      newAddedInfluencer.campaign_influencer_id = itemData.campaign_influencer_id;
      newAddedInfluencer.influencerStatus = itemData.influencerStatus ? itemData.influencerStatus : null;
      newAddedInfluencer.isNewAdded = true;
      return newAddedInfluencer;
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/services/campaign/campaign.service.ts